import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Spin, theme } from 'antd';

import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';

type OfficeKind = 'docx' | 'xlsx' | 'legacy' | 'unknown';

interface OfficePreviewPaneProps {
  downloadUrl: string;
  fileName?: string;
}

const OfficePreviewPane: FC<OfficePreviewPaneProps> = ({ downloadUrl, fileName }) => {
  const { token } = theme.useToken();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);

  const normalizedFileName = useMemo(() => (fileName || '').trim(), [fileName]);
  const extension = useMemo(() => {
    const parts = normalizedFileName.toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }, [normalizedFileName]);

  const kind = useMemo<OfficeKind>(() => {
    if (extension === 'docx') return 'docx';
    if (extension === 'xlsx') return 'xlsx';
    if (extension === 'doc' || extension === 'xls') return 'legacy';
    return 'unknown';
  }, [extension]);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      setHtml(null);

      try {
        if (kind === 'legacy') {
          throw new Error('Legacy Office formats (.doc/.xls) are not supported for in-app preview. Please download.');
        }
        if (kind === 'unknown') {
          throw new Error('Unsupported Office file type for in-app preview.');
        }

        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Failed to load file (${response.status})`);
        }
        const arrayBuffer = await response.arrayBuffer();

        if (kind === 'docx') {
          const mammoth = await import('mammoth/mammoth.browser');
          const result = await mammoth.convertToHtml({ arrayBuffer });
          const docHtml = result?.value || '';
          const wrapped = buildSandboxedHtml(normalizedFileName || 'Document', docHtml);
          if (!isCancelled) setHtml(wrapped);
          return;
        }

        if (kind === 'xlsx') {
          const ExcelJS = await import('exceljs');
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);

          const firstWorksheet = workbook.worksheets?.[0];
          if (!firstWorksheet) {
            throw new Error('No worksheets found in workbook.');
          }

          const sheetHtml = buildWorksheetHtml(firstWorksheet, { maxRows: 200, maxColumns: 50 });
          const wrapped = buildSandboxedHtml(normalizedFileName || 'Workbook', sheetHtml);
          if (!isCancelled) setHtml(wrapped);
          return;
        }
      } catch (e) {
        if (!isCancelled) {
          setError(e instanceof Error ? e.message : 'Failed to render Office preview.');
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [downloadUrl, kind, normalizedFileName]);

  if (isLoading) {
    return (
      <FlexBox justifyCenter stretch style={{ padding: 40 }}>
        <Spin size="large" />
      </FlexBox>
    );
  }

  if (error) {
    return (
      <FlexCol stretch style={{ padding: 20 }}>
        <Alert
          type="info"
          showIcon
          title="Office preview unavailable"
          description={error}
        />
      </FlexCol>
    );
  }

  if (!html) {
    return (
      <FlexCol stretch style={{ padding: 20 }}>
        <Alert type="info" showIcon title="No preview content." />
      </FlexCol>
    );
  }

  return (
    <FlexCol stretch style={{ height: '100%', background: token.colorBgLayout }}>
      <iframe
        title="Office Attachment Preview"
        sandbox=""
        srcDoc={html}
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </FlexCol>
  );
};

function buildSandboxedHtml(title: string, bodyHtml: string) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(title)}</title>
    <style>
      html, body { height: 100%; }
      body { margin: 0; padding: 16px; font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; background: white; color: #111; }
      img { max-width: 100%; height: auto; }
      table { border-collapse: collapse; width: max-content; max-width: 100%; }
      td, th { border: 1px solid #ddd; padding: 6px 8px; vertical-align: top; white-space: nowrap; }
      #sheet { overflow: auto; max-width: 100%; }
    </style>
  </head>
  <body>${bodyHtml}</body>
</html>`;
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function buildWorksheetHtml(
  worksheet: { actualRowCount: number; actualColumnCount: number; getRow: (rowNumber: number) => any },
  limits: { maxRows: number; maxColumns: number }
) {
  const rowCount = Math.min(worksheet.actualRowCount || 0, limits.maxRows);
  const columnCount = Math.min(worksheet.actualColumnCount || 0, limits.maxColumns);

  if (rowCount === 0 || columnCount === 0) {
    return '<div id="sheet">No cells found in worksheet.</div>';
  }

  const rowsHtml: string[] = [];
  for (let rowNumber = 1; rowNumber <= rowCount; rowNumber++) {
    const row = worksheet.getRow(rowNumber);
    const cellsHtml: string[] = [];
    for (let colNumber = 1; colNumber <= columnCount; colNumber++) {
      const cell = row.getCell(colNumber);
      const text = escapeHtml(formatExcelCellValue(cell?.value));
      const tag = rowNumber === 1 ? 'th' : 'td';
      cellsHtml.push(`<${tag}>${text}</${tag}>`);
    }
    rowsHtml.push(`<tr>${cellsHtml.join('')}</tr>`);
  }

  const maybeTruncated =
    worksheet.actualRowCount > limits.maxRows || worksheet.actualColumnCount > limits.maxColumns
      ? `<div style="margin-bottom: 12px; color: #666;">Preview limited to ${limits.maxRows} rows × ${limits.maxColumns} columns.</div>`
      : '';

  return `<div id="sheet">${maybeTruncated}<table><tbody>${rowsHtml.join('')}</tbody></table></div>`;
}

function formatExcelCellValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (value instanceof Date) return value.toLocaleString();

  if (typeof value === 'object') {
    if ('text' in (value as any) && typeof (value as any).text === 'string') {
      return (value as any).text;
    }
    if ('richText' in (value as any) && Array.isArray((value as any).richText)) {
      return (value as any).richText.map((part: any) => (typeof part?.text === 'string' ? part.text : '')).join('');
    }
    if ('hyperlink' in (value as any) && typeof (value as any).hyperlink === 'string') {
      const label = typeof (value as any).text === 'string' ? (value as any).text : (value as any).hyperlink;
      return label;
    }
    if ('formula' in (value as any)) {
      const result = (value as any).result;
      return typeof result === 'string' ? result : result == null ? '' : String(result);
    }
  }

  return String(value);
}

export default OfficePreviewPane;

